import {
  getDefaultCompetitionId,
  resolveCompetitionFilters,
  resolveSeasonFilters,
} from '@statsbomb/kitbag-global-football/state';
import { atom, Getter } from 'jotai';
import { unwrap } from 'jotai/utils';
import { comparisonFiltersAtom } from '@/atoms/filters/comparison';
import {
  playerComparisonCyclesAtom,
  playerComparisonCyclesOrderedAtom,
  rawPlayerComparisonCyclesAtom,
} from '@/atoms/queries/player/playerComparisonCycles';
import { rawPlayerComparisonTeamsAtom } from '@/atoms/queries/player/playerComparisonTeams';
import { convertOptionsToValues, isSharingId } from '@/utils/array';
import { createCompetitionOptionsAtom, createSeasonOptionsAtom } from '@/utils/atoms/cycles';
import { filterCyclesBy, filterCyclesWithRelsByCompetition } from '@/utils/filters';
import { convertToDropdownOption } from '@/utils/object';
import { objSnakeToCamel } from '@/utils/queries';

export const playerComparisonCompetitionOptionsAtom = createCompetitionOptionsAtom(playerComparisonCyclesAtom);

const unwrappedComparisonCompetitionOptionsAtom = unwrap(playerComparisonCompetitionOptionsAtom, prev => prev || []);

const readCompetitionOptions = (get: Getter) => {
  try {
    return get(unwrappedComparisonCompetitionOptionsAtom);
  } catch {
    return [];
  }
};

const comparisonDefaultCompetitionIdAtom = atom(async get =>
  getDefaultCompetitionId(await get(playerComparisonCyclesOrderedAtom)),
);

const unwrappedComparisonDefaultCompetitionIdAtom = unwrap(comparisonDefaultCompetitionIdAtom, prev => prev || null);

const readDefaultCompetitionId = (get: Getter) => {
  try {
    return get(unwrappedComparisonDefaultCompetitionIdAtom);
  } catch {
    return null;
  }
};

export const comparisonSelectedCompetitionsAtom = atom(get => {
  const competitionOptions = readCompetitionOptions(get);
  return resolveCompetitionFilters({
    selectedCompetitionIds: get(comparisonFiltersAtom).competitionIds,
    competitionOptions,
    defaultCompetitionId: readDefaultCompetitionId(get),
  }).selectedCompetitions;
});

export const comparisonCompetitionIdsToFilterByAtom = atom(get => {
  const competitionOptions = readCompetitionOptions(get);
  return resolveCompetitionFilters({
    selectedCompetitionIds: get(comparisonFiltersAtom).competitionIds,
    competitionOptions,
    defaultCompetitionId: readDefaultCompetitionId(get),
  }).competitionIdsToFilterBy;
});

const filteredPlayerComparisonCyclesSeasonAtom = atom(async get => {
  const cycles = await get(playerComparisonCyclesAtom);
  const selectedCompetitionIds = get(comparisonCompetitionIdsToFilterByAtom);
  return filterCyclesWithRelsByCompetition(cycles, selectedCompetitionIds);
});

export const playerComparisonSeasonOptionsAtom = createSeasonOptionsAtom(filteredPlayerComparisonCyclesSeasonAtom);

const unwrappedComparisonSeasonOptionsAtom = unwrap(playerComparisonSeasonOptionsAtom, prev => prev || null);

const readSeasonOptions = (get: Getter) => {
  try {
    return get(unwrappedComparisonSeasonOptionsAtom);
  } catch {
    return null;
  }
};

export const comparisonSelectedSeasonsAtom = atom(get => {
  const seasonOptions = readSeasonOptions(get);
  return resolveSeasonFilters({
    selectedSeasonIds: get(comparisonFiltersAtom).seasonIds,
    seasonOptions,
    currentYear: new Date().getFullYear(),
  }).selectedSeasons;
});

export const comparisonSeasonIdsToFilterByAtom = atom(get => {
  const selectedSeasonIds = get(comparisonFiltersAtom).seasonIds;
  if (selectedSeasonIds) return selectedSeasonIds;
  return convertOptionsToValues(get(comparisonSelectedSeasonsAtom));
});

export const playerComparisonTeamsAtom = atom(async get => {
  const rawCycles = (await get(rawPlayerComparisonCyclesAtom)).data;
  const rawTeams = (await get(rawPlayerComparisonTeamsAtom)).data;
  const selectedCompetitionIds = get(comparisonCompetitionIdsToFilterByAtom);
  const selectedSeasonIds = get(comparisonSeasonIdsToFilterByAtom);

  const cycles = rawCycles.map(({ cycle }) => cycle);
  const filteredByComp = filterCyclesBy(cycles, selectedCompetitionIds, 'competition_id');
  const filteredBySeasons = filterCyclesBy(filteredByComp, selectedSeasonIds, 'season_id');
  const validCycleIds = filteredBySeasons.map(cycle => cycle.cycle_id);

  return selectedCompetitionIds.length || selectedSeasonIds.length
    ? rawTeams.filter(team => isSharingId(team.cycle_ids, validCycleIds))
    : rawTeams;
});

export const playerComparisonTeamsOptionsAtom = atom(async get => {
  const teams = await get(playerComparisonTeamsAtom);
  return teams
    .map(objSnakeToCamel)
    .map(({ name, teamId, areaCode }) => convertToDropdownOption(name, teamId, { countryCode: areaCode }));
});

const unwrappedComparisonTeamOptionsAtom = unwrap(playerComparisonTeamsOptionsAtom, prev => prev || []);

export const unwrappedComparisonPlayerTeamAtom = atom(get => {
  const teams = get(unwrappedComparisonTeamOptionsAtom);
  return teams[0]?.label || '';
});

export const comparisonSelectedTeamsAtom = atom(get => {
  const teamOptions = get(unwrappedComparisonTeamOptionsAtom);
  const selectedTeamIds = get(comparisonFiltersAtom).teamIds;
  return teamOptions.filter(({ value }) => selectedTeamIds.includes(value));
});

const comparisonCompetitionsLoadedAtom = atom(async get => {
  await get(playerComparisonCompetitionOptionsAtom);
  return true;
});
export const haveComparisonCompetitionOptionsLoadedAtom = unwrap(
  comparisonCompetitionsLoadedAtom,
  prev => prev || false,
);

const comparisonSeasonsLoadedAtom = atom(async get => {
  await get(playerComparisonSeasonOptionsAtom);
  return true;
});
export const haveComparisonSeasonOptionsLoadedAtom = unwrap(comparisonSeasonsLoadedAtom, prev => prev || false);
